import { makeAutoObservable } from "mobx";
import {
  CarrinhoItem,
  ClienteData,
  CobrancaPixResult,
  FormErrors,
  OrderDetails,
  ProdutoCategoria,
  Store as StoreType,
} from "../types/store";
import { formatBRL } from "../utils";
import { Produto } from "../types/store";
import { API_BASE_URL } from "../config";

class Store implements StoreType {
  products: Produto[] = [];
  cartItems: CarrinhoItem[] = [];
  isCartOpen = false;
  isCheckoutOpen = false;
  checkoutCep = "";
  checkoutFrete: number | null = null;
  checkoutEnvio = "";
  clienteData: ClienteData = {
    name: "",
    email: "",
    cpf_cnpj: "",
    endereco: "",
    cidade: "",
    estado: "",
    cep: "",
    numero: "",
    complemento: "",
  };
  formErrors: FormErrors = {};
  isSubmitting = false;
  navigate: ((path: string) => void) | null = null;

  constructor() {
    makeAutoObservable(this);
    this.loadCartFromStorage();
  }

  private loadCartFromStorage = async (): Promise<void> => {
    try {
      const savedCart = localStorage.getItem("cartItems");
      if (savedCart) {
        const parsedCart: CarrinhoItem[] = JSON.parse(savedCart);

        // Validate each item with latest product data
        const validatedCart: CarrinhoItem[] = [];
        for (const item of parsedCart) {
          const latest = await this.loadProdutoById(item.id, true);
          if (latest && latest.ativo && latest.quantidadeEstoque > 0) {
            // Optionally, update estoque if changed
            validatedCart.push({
              ...item,
              quantidade: Math.min(item.quantidade, latest.quantidadeEstoque),
              preco: latest.preco,
              desconto: latest.desconto,
            });
          }
          // If product is not valid anymore, do not add to cart
        }
        this.cartItems = validatedCart;
      }
    } catch (error) {
      console.error("Error loading cart from storage:", error);
    }
  };

  private saveCartToStorage = (): void => {
    try {
      localStorage.setItem("cartItems", JSON.stringify(this.cartItems));
    } catch (error) {
      console.error("Error saving cart to storage:", error);
    }
  };

  setNavigate(navigate: (path: string) => void): void {
    this.navigate = navigate;
  }

  toggleCart = (): void => {
    this.isCartOpen = !this.isCartOpen;
  };

  addToCart = (product: Produto): void => {
    const existingItem = this.cartItems.find((item) => item.id === product.id);
    if (existingItem) {
      existingItem.quantidade += 1;
    } else {
      this.cartItems.push({ ...product, quantidade: 1 });
    }
    this.isCartOpen = true;
    this.saveCartToStorage();
  };

  removeFromCart = (productId: number): void => {
    this.cartItems = this.cartItems.filter((item) => item.id !== productId);
    if (this.cartItems.length === 0) {
      this.isCartOpen = false;
    }
    this.saveCartToStorage();
  };

  updateQuantity = (productId: number, quantity: number): void => {
    if (quantity < 1) return;
    const item = this.cartItems.find((item) => item.id === productId);
    if (item) {
      item.quantidade = quantity;
      this.saveCartToStorage();
    }
  };

  startCheckout = (cep: string, frete: number, envio: string): void => {
    if (this.cartItems.length === 0) return;
    this.isCheckoutOpen = true;
    this.checkoutFrete = frete;
    this.checkoutEnvio = envio;
    this.checkoutCep = cep;
    if (this.navigate) {
      this.navigate("/checkout");
    }
  };

  cancelCheckout = (): void => {
    this.isCheckoutOpen = false;
    if (this.navigate) {
      this.navigate("/");
    }
  };

  confirmOrder = async (
    forma_envio_id: number,
    forma_pagamento_id: number,
    preco_frete: number
  ): Promise<number> => {
    try {
      if (!this.validateForm()) return 0;
      // Monta os dados do cliente a partir do formulário
      const { name, email, cpf_cnpj, cep, endereco, numero, complemento, cidade, estado } =
        this.clienteData;

      // Monta os itens do pedido
      const items = this.cartItems.map((item) => ({
        produto_id: item.id,
        quantidade: item.quantidade,
      }));

      // Monta o corpo da requisição
      const body = {
        cliente_nome: name,
        cliente_email: email,
        cliente_cpf_cnpj: cpf_cnpj,
        cliente_cep: cep,
        cliente_endereco: endereco,
        cliente_numero_casa: numero,
        cliente_complemento: complemento || "",
        cliente_cidade: cidade,
        cliente_estado: estado,
        forma_envio_id,
        forma_pagamento_id,
        items,
        preco_frete
      };

      const res = await fetch(`${API_BASE_URL}/pedidos/confirmarPedido`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        throw new Error("Erro ao confirmar pedido");
      }

      // Limpa carrinho e reset form
      this.cartItems = [];
      this.saveCartToStorage(); // Clear cart from storage
      this.clienteData = {
        name: "",
        email: "",
        cpf_cnpj: "",
        endereco: "",
        cidade: "",
        estado: "",
        cep: "",
        numero: "",
        complemento: "",
      };
      const data = await res.json();
      return data.pedido_id;
    } catch (error) {
      console.error("Erro ao confirmar pedido:", error);
      return 0;
    }
  };

  gerarCobrancaPix = async (pedidoId: number): Promise<CobrancaPixResult | null> => {
    try {
      const res = await fetch(`${API_BASE_URL}/pedidos/gerarCobrancaPix`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ pedido_id: pedidoId }),
      });

      if (!res.ok) {
        throw new Error("Erro ao gerar cobrança PIX");
      }

      return await res.json();
    } catch (error) {
      console.error("Erro ao gerar cobrança PIX:", error);
      return null;
    }
  };

  updateCustomerData = <K extends keyof ClienteData>(
    field: K,
    value: ClienteData[K]
  ): void => {
    this.clienteData[field] = value;
    if (this.formErrors[field]) {
      this.formErrors[field] = "";
    }
  };

  validateForm = (): boolean => {
    const errors: FormErrors = {};
    const { name, email, endereco, cidade, estado, cep, numero } =
      this.clienteData;

    if (!name.trim()) errors.name = "Nome é obrigatório";
    if (!email.trim()) errors.email = "E-mail é obrigatório";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      errors.email = "E-mail inválido";
    if (!endereco.trim()) errors.address = "Endereço é obrigatório";
    if (!cidade.trim()) errors.city = "Cidade é obrigatória";
    if (!estado) errors.state = "Estado é obrigatório";
    if (!cep.trim()) errors.zipCode = "CEP é obrigatório";
    if (!numero.trim()) errors.number = "Número é obrigatório";

    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  };

  get cartItemsCount(): number {
    return this.cartItems.reduce((total, item) => total + item.quantidade, 0);
  }

  get cartTotal(): number {
    return this.cartItems.reduce(
      (total, item) => total + (item.preco - item.desconto) * item.quantidade,
      0
    );
  }

  get formattedCartTotal(): string {
    return formatBRL(this.cartTotal);
  }

  getProductsByCategory(categoria: ProdutoCategoria) {
    return this.products.filter((product) => product.categoria === categoria);
  }

  getProductsById(id: number) {
    return this.products.find((product) => product.id === id) ?? null;
  }

  async loadCatalogo() {
    try {
      const response = await fetch(
        `${API_BASE_URL}/produtos/loja?categoria=${ProdutoCategoria.CATALOGO}`
      );
      if (!response.ok) {
        throw new Error("Erro ao carregar catálogo");
      }
      const data = await response.json();
      this.products = data;
    } catch (error) {
      console.error("Erro ao carregar catálogo:", error);
    }
  }

  async loadLoja() {
    try {
      const response = await fetch(
        `${API_BASE_URL}/produtos/loja?categoria=${ProdutoCategoria.LOJA}`
      );
      if (!response.ok) {
        throw new Error("Erro ao carregar loja");
      }
      const data = await response.json();
      this.products = data;
    } catch (error) {
      console.error("Erro ao carregar loja:", error);
    }
  }

  async loadProdutoById(id: number, skipAddingToProducts?: boolean): Promise<Produto | null> {
    try {
      const existingProduct = this.products.find(
        (produto) => produto.id === id
      );
      if (existingProduct) {
        return existingProduct;
      }

      const response = await fetch(`${API_BASE_URL}/produtos/${id}`);
      if (!response.ok) {
        throw new Error("Erro ao carregar produto");
      }
      const produto = (await response.json()) as Produto;
      produto.preco = Number(produto.preco);
      produto.desconto = Number(produto.desconto);
      produto.peso = Number(produto.peso);
      if (!skipAddingToProducts)
        this.products.push(produto);
      return produto;
    } catch (error) {
      console.error("Erro ao carregar produto:", error);
      return null;
    }
  }

  async calculaFrete(codProdutos: number[], cep: string, envioType: string) {
    try {
      const res = await fetch(`${API_BASE_URL}/pedidos/calculateFrete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          codProdutos,
          tipoEnvio: envioType,
          cepDestino: cep,
        }),
      });

      if (!res.ok) {
        throw new Error("Erro ao calcular frete");
      }

      const data = await res.json();
      // Espera-se que o backend retorne { custoFrete, prazoFrete }
      return {
        custoFrete: Number(
          typeof data.precoFrete === "string"
            ? data.precoFrete.replace(",", ".")
            : data.precoFrete
        ),
        prazoFrete: Number(data.prazoFrete),
      };
    } catch (error) {
      console.error("Erro ao calcular frete:", error);
      throw error;
    }
  }

  async checkOrder(orderId: number, email: string) {
    try {
      const res = await fetch(`${API_BASE_URL}/pedidos/statusPedido`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          id: orderId,
          cliente_email: email,
        }),
      });

      if (!res.ok) {
        throw new Error("Erro ao calcular frete");
      }

      const data = await res.json();

      return {
        id: orderId,
        statusHistory: data.atualizacoes.map((update: any) => {
          return {
            status: update.status_nome,
            date: update.date,
            concluida: update.concluido,
          };
        }),
      };
    } catch (error) {
      console.error("Erro ao verificar pedido:", error);
      throw error;
    }
  }

  updateProduct = (updatedProduct: Produto): void => {
    const idx = this.products.findIndex((p: Produto) => p.id === updatedProduct.id);
    if (idx !== -1) {
      this.products[idx] = updatedProduct;
    }
  };

  loadProducts = (): void => {
    // This would typically be an API call
    // this.products = [
    // {
    //   id: 1,
    //   nome: "Pindaíba Volume 1 O Pão Nosso de Cada Dia 2",
    //   categoria: ProdutoCategoria.CATALOGO,
    //   autores: "Mancha Teixeira e Vina Adamante",
    //   descricao:
    //     "Testemunhe a emocionante rotina do cidadão pindaibano em busca do Pão Nosso de Cada Dia.",
    //   preco: 59.9,
    //   desconto: 10,
    //   informacaoAdicional:
    //     "240 páginas PB em papel pólen<br>" +
    //     "16,0 cm x 23,0 cm<br><b>ISBN</b>: 0123456788",
    //   peso: 0.8,
    //   fotoCapaUrl:
    //     "https://rabodegaloeditora.com.br/images/rabo-de-galo-editora_pindaiba-1.png",
    //   fotoProdutosUrls: [
    //     "https://rabodegaloeditora.com.br/images/rabo-de-galo-editora_pindaiba-1.png",
    //     "https://rabodegaloeditora.com.br/images/rabo-de-galo-editora_pindaiba-1.png",
    //   ],
    // },
    // {
    //   id: 2,
    //   nome: "Pindaíba Volume 2 O Pão Nosso de Cada Dia",
    //   categoria: ProdutoCategoria.CATALOGO,
    //   autores: "Mancha Teixeira e Vina Adamante",
    //   descricao:
    //     "Testemunhe a emocionante rotina do cidadão pindaibano em busca do Pão Nosso de Cada Dia.",
    //   preco: 59.9,
    //   desconto: 10,
    //   informacaoAdicional:
    //     "240 páginas PB em papel pólen<br>" +
    //     "16,0 cm x 23,0 cm<br><b>ISBN</b>: 0123456788",
    //   peso: 0.8,
    //   fotoCapaUrl:
    //     "https://rabodegaloeditora.com.br/images/rabo-de-galo-editora_pindaiba-1.png",
    //   fotoProdutosUrls: [
    //     "https://rabodegaloeditora.com.br/images/rabo-de-galo-editora_pindaiba-1.png",
    //     "https://rabodegaloeditora.com.br/images/rabo-de-galo-editora_pindaiba-1.png",
    //   ],
    // },
    // {
    //   id: 3,
    //   nome: "Pindaíba Volume 3 O Pão Nosso de Cada Dia",
    //   categoria: ProdutoCategoria.CATALOGO,
    //   autores: "Mancha Teixeira e Vina Adamante",
    //   descricao:
    //     "Testemunhe a emocionante rotina do cidadão pindaibano em busca do Pão Nosso de Cada Dia.",
    //   preco: 59.9,
    //   desconto: 10,
    //   informacaoAdicional:
    //     "240 páginas PB em papel pólen<br>" +
    //     "16,0 cm x 23,0 cm<br><b>ISBN</b>: 0123456788",
    //   peso: 0.8,
    //   fotoCapaUrl:
    //     "https://rabodegaloeditora.com.br/images/rabo-de-galo-editora_pindaiba-1.png",
    //   fotoProdutosUrls: [
    //     "https://rabodegaloeditora.com.br/images/rabo-de-galo-editora_pindaiba-1.png",
    //     "https://rabodegaloeditora.com.br/images/rabo-de-galo-editora_pindaiba-1.png",
    //   ],
    // },
    // {
    //   id: 4,
    //   nome: "Pindaíba Volume 4 O Pão Nosso de Cada Dia",
    //   categoria: ProdutoCategoria.CATALOGO,
    //   autores: "Mancha Teixeira e Vina Adamante",
    //   descricao:
    //     "Testemunhe a emocionante rotina do cidadão pindaibano em busca do Pão Nosso de Cada Dia.",
    //   preco: 59.9,
    //   desconto: 10,
    //   informacaoAdicional:
    //     "240 páginas PB em papel pólen<br>" +
    //     "16,0 cm x 23,0 cm<br><b>ISBN</b>: 0123456788",
    //   peso: 0.8,
    //   fotoCapaUrl:
    //     "https://rabodegaloeditora.com.br/images/rabo-de-galo-editora_pindaiba-1.png",
    //   fotoProdutosUrls: [
    //     "https://rabodegaloeditora.com.br/images/rabo-de-galo-editora_pindaiba-1.png",
    //     "https://rabodegaloeditora.com.br/images/rabo-de-galo-editora_pindaiba-1.png",
    //   ],
    // },
    // {
    //   id: 5,
    //   nome: "Pindaíba Volume 5 O Pão Nosso de Cada Dia",
    //   categoria: ProdutoCategoria.CATALOGO,
    //   autores: "Mancha Teixeira e Vina Adamante",
    //   descricao:
    //     "Testemunhe a emocionante rotina do cidadão pindaibano em busca do Pão Nosso de Cada Dia.",
    //   preco: 59.9,
    //   desconto: 10,
    //   informacaoAdicional:
    //     "240 páginas PB em papel pólen<br>" +
    //     "16,0 cm x 23,0 cm<br><b>ISBN</b>: 0123456788",
    //   peso: 0.8,
    //   fotoCapaUrl:
    //     "https://rabodegaloeditora.com.br/images/rabo-de-galo-editora_pindaiba-1.png",
    //   fotoProdutosUrls: [
    //     "https://rabodegaloeditora.com.br/images/rabo-de-galo-editora_pindaiba-1.png",
    //     "https://rabodegaloeditora.com.br/images/rabo-de-galo-editora_pindaiba-1.png",
    //   ],
    // },
    // ];
  };
}

// Create a single instance of the store
export const store = new Store();
